import React, { useRef, useState, useMemo } from 'react';
import numeral from 'numeral';
import LinearProgress from '@material-ui/core/LinearProgress';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CloudUpload from '@material-ui/icons/CloudUpload';
import styles from 'assets/jss/material-dashboard-pro-react/components/fileUploadStyle';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { translate } from 'components/Translation';
import { BodyM, Caption } from 'components/Typography/Typography';
import Button from 'components/CustomButtons/Button';

//@ts-ignore
const useStyles = makeStyles(styles);

interface IFileUploadProps {
	text: string;
	subtext?: string;
	onFileChange: (e: any) => void;
	onMetadataChange?: ({ name }: { name: string }) => void;
	loading?: boolean;
	loadingText?: string;
	errorMessage?: string;
	iconPosition?: 'normal' | 'right';
	className?: string;
	size?: 'sm' | 'md';
	textSize?: 'sm' | 'md' | 'subtext';
	fileMaxSize?: number; // in bytes
	insideError?: boolean;
	acceptedExtensions?: string[];
	showUploadButton?: boolean;
	multipleFilesAllowed?: boolean;
	setMultipleFilesErrors?: React.Dispatch<React.SetStateAction<MultipleFilesUploadError[]>>;
	uploadDisabled?: boolean;
}

export enum FileUploadError {
	MAX_SIZE_EXCEEDED = 'MAX_SIZE_EXCEEDED',
	WRONG_EXTENSION = 'WRONG_EXTENSION',
	EMPTY_FILE = 'EMPTY_FILE',
	TOO_MANY_FILES = 'TOO_MANY_FILES',
	BAD_SHEET = 'BAD_SHEET',
	INVALID_ACCOUNT_NUMBER = 'INVALID_ACCOUNT_NUMBER',
}

export type MultipleFilesUploadError = {
	fileName: string;
	fileUploadError: FileUploadError;
};

const FileUpload = ({
	text,
	subtext = '',
	onFileChange,
	onMetadataChange,
	loading,
	loadingText,
	errorMessage,
	iconPosition = 'normal',
	className = '',
	size = 'md',
	fileMaxSize,
	textSize = 'sm',
	insideError = false,
	acceptedExtensions,
	showUploadButton = false,
	multipleFilesAllowed = false,
	setMultipleFilesErrors,
	uploadDisabled,
}: IFileUploadProps) => {
	const classes = useStyles();
	const [drag, setDrag] = useState(false);
	const [error, setError] = useState<FileUploadError | null>(null);
	const fileInput = useRef<any>(null);

	const handleFileChange = (event: any) => {
		if (multipleFilesAllowed) {
			handleMultipleFileChange(event);
		} else {
			handleSingleFileChange(event);
		}
	};

	const handleMultipleFileChange = (event: any) => {
		event.preventDefault();
		event.persist();
		const files = event.target.files;
		const errors: MultipleFilesUploadError[] = [];
		const formData = new FormData();
		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			if (fileMaxSize && file.size > fileMaxSize) {
				errors.push({ fileName: file.name, fileUploadError: FileUploadError.MAX_SIZE_EXCEEDED });
			} else if (acceptedExtensions && acceptedExtensions.indexOf(file.type) < 0) {
				errors.push({ fileName: file.name, fileUploadError: FileUploadError.WRONG_EXTENSION });
			} else if (file.size === 0) {
				errors.push({ fileName: file.name, fileUploadError: FileUploadError.EMPTY_FILE });
			} else {
				formData.append(file.name, file);
			}
		}
		//@typescript-eslint/no-unused-expressions
		setMultipleFilesErrors?.(errors);
		onFileChange(formData);
		event.target.value = '';
	};

	const handleSingleFileChange = (event: any) => {
		event.preventDefault();
		event.persist();
		const reader = new FileReader();
		const file = event.target.files[0];
		const extension = file.type;
		if (file) {
			reader.onloadend = () => {
				onFileChange(reader.result);
				event.target.value = null;
			};
			if (fileMaxSize && file.size > fileMaxSize) {
				setError(FileUploadError.MAX_SIZE_EXCEEDED);
			} else if (acceptedExtensions && acceptedExtensions.indexOf(extension) < 0) {
				setError(FileUploadError.WRONG_EXTENSION);
			} else if (file.size === 0) {
				setError(FileUploadError.EMPTY_FILE);
			} else {
				setError(null);
				onMetadataChange && onMetadataChange({ name: file.name });
				reader.readAsDataURL(file);
			}
		}
	};

	const errorAfterFileUploaded: string = useMemo(() => {
		if (error === FileUploadError.MAX_SIZE_EXCEEDED) {
			return translate('file.too.big', numeral(fileMaxSize).format('0.0b'));
		}
		if (error === FileUploadError.EMPTY_FILE) {
			return translate('error.file.is.empty');
		}
		if (error === FileUploadError.WRONG_EXTENSION) {
			return translate('error.invalid.file.extension');
		}
		return '';
	}, [error, fileMaxSize]);

	return (
		<div className={classNames({ [className]: !!className })}>
			<ConditionalRender
				show={!!errorMessage && !insideError}
				onTrue={() => (
					<div className={classes.errorWrapper}>
						<BodyM color="danger">{errorMessage}</BodyM>
					</div>
				)}
			/>
			<div
				className={classNames({
					[classes.fileInputWrapper]: !loading,
					[classes[size]]: !loading,
					[classes.loading]: loading,
				})}
			>
				<input
					type="file"
					disabled={uploadDisabled}
					multiple={multipleFilesAllowed}
					onChange={handleFileChange}
					ref={fileInput}
					onDragEnter={() => setDrag(true)}
					onDragLeave={() => setDrag(false)}
					onDrop={() => setDrag(false)}
				/>
				{loading ? (
					<div className={classes.loadingIndicatorWrapper}>
						<LinearProgress />
						<Caption color="brand">{loadingText}</Caption>
					</div>
				) : (
					<div
						className={classNames({
							[classes.fileInputContent]: true,
							[classes.drag]: drag,
						})}
					>
						<CloudUpload
							className={classNames(classes[`icon${size}`], {
								[classes.iconRight]: iconPosition === 'right',
							})}
						/>
						<ConditionalRender show={textSize === 'sm'}>
							<Caption color="secondary-light">{text}</Caption>
							<ConditionalRender show={!!errorAfterFileUploaded}>
								<div className={classes.errorMessageWrapper}>
									<Caption color="danger">{errorAfterFileUploaded}</Caption>
								</div>
							</ConditionalRender>
						</ConditionalRender>
						<ConditionalRender show={textSize === 'md'}>
							<BodyM color="secondary-light">{text}</BodyM>
							<ConditionalRender show={!!errorAfterFileUploaded}>
								<div className={classes.errorMessageWrapper}>
									<BodyM color="danger">{errorAfterFileUploaded}</BodyM>
								</div>
							</ConditionalRender>
						</ConditionalRender>
						<ConditionalRender show={textSize === 'subtext'}>
							<BodyM color="enterprise">{text}</BodyM>
							<Caption color="secondary-light">{subtext}</Caption>
							<ConditionalRender show={!!errorAfterFileUploaded}>
								<div className={classes.errorMessageWrapper}>
									<BodyM color="danger">{errorAfterFileUploaded}</BodyM>
								</div>
							</ConditionalRender>
						</ConditionalRender>
						<ConditionalRender
							show={!!errorMessage && insideError}
							onTrue={() => <Caption color="danger">{errorMessage}</Caption>}
						/>
						<ConditionalRender
							show={showUploadButton}
							onTrue={() => (
								<Button color="tertiary-light" className={classes.uploadButton}>
									{translate('upload.file')}
								</Button>
							)}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default FileUpload;
